import React, { useRef, useState, useCallback } from 'react'
import * as echarts from 'echarts'
import 'echarts-gl'
import {
  downloadChartAsPng,
  sanitizeFilename,
} from '../../../utils/chartExport'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu'
import {
  SectionAnalysisContentContainer,
  AnalysisTabContentContainer,
} from '../../molecules/SectionAnalysisStyles'
import { Container, Card, CardTitle } from './GeoAnalysisSection.styles'
import { getScoreVariant, type AltimetryData } from './utils'
import { useGeometryMetrics } from './hooks/core'
import { useTopographyChart, usePolygonChart } from './hooks/charts'
import { useChartResize } from './hooks/effects'
import { OverviewTab, TopographyTab, ShapeTab } from './tabs'
import type {
  GeoAnalysisSectionLand,
  GeoAnalysisSectionTranslations,
  GeoAnalysisSectionProps,
  ExtraTab,
} from './GeoAnalysisSection.types'

// Re-export types for external use
export type {
  GeoAnalysisSectionLand,
  GeoAnalysisSectionTranslations,
  GeoAnalysisSectionProps,
  ExtraTab,
  AltimetryData,
}

// Animation for smooth transitions

export const GeoAnalysisSection: React.FC<GeoAnalysisSectionProps> = ({
  land,
  altimetryData = null,
  terrainAltimetryData = null,
  loading = false,
  altimetryLoading = false,
  error = null,
  translations,
  extraTabs,
}) => {
  const [activeTab, setActiveTab] = useState('overview')
  const topographyChartRef = useRef<HTMLDivElement>(null)
  const polygonChartRef = useRef<HTMLDivElement>(null)

  // Chart instance refs for PNG export
  const topographyInstanceRef = useRef<echarts.ECharts | null>(null)
  const polygonInstanceRef = useRef<echarts.ECharts | null>(null)

  // Calculate all geometry metrics using actual data and Turf.js
  const metrics = useGeometryMetrics(land)

  // Altimetry data is now provided by external data context

  // Initialize charts using dedicated hooks
  useTopographyChart({
    chartRef: topographyChartRef,
    activeTab,
    altimetryData,
    altimetryLoading,
    translations,
    chartInstanceRef: topographyInstanceRef,
  })

  usePolygonChart({
    chartRef: polygonChartRef,
    activeTab,
    land,
    translations,
    chartInstanceRef: polygonInstanceRef,
  })

  // Handle window resize for all charts
  useChartResize({
    chartRefs: [topographyChartRef, polygonChartRef],
  })

  // Download chart as PNG helper
  const handleDownload = useCallback(
    (instance: echarts.ECharts | null, name: string) => {
      if (!instance || instance.isDisposed()) return
      const dataUrl = instance.getDataURL({
        type: 'png',
        pixelRatio: 3,
        backgroundColor: '#fff',
      })
      downloadChartAsPng(dataUrl, sanitizeFilename(name))
    },
    []
  )

  const handleDownloadTopography = useCallback(
    () =>
      handleDownload(
        topographyInstanceRef.current,
        translations?.sections?.topographicVisualization || 'topography-3d'
      ),
    [handleDownload, translations]
  )

  const handleDownloadPolygon = useCallback(
    () =>
      handleDownload(
        polygonInstanceRef.current,
        translations?.sections?.plotBoundaryVisualization ||
          'plot-boundary-visualization'
      ),
    [handleDownload, translations]
  )

  if (loading) {
    return (
      <Container>
        <Card>
          <CardTitle>
            {translations?.loading || 'Loading geographical data...'}
          </CardTitle>
        </Card>
      </Container>
    )
  }

  if (error || !land || !metrics) {
    return (
      <Container>
        <Card>
          <CardTitle>
            {translations?.error || 'Unable to load geographical data'}
          </CardTitle>
        </Card>
      </Container>
    )
  }

  const tabs = [
    {
      id: 'overview',
      label: translations?.tabs?.overview || 'Overview',
      icon: 'layout-dashboard',
    },
    {
      id: 'topography',
      label: translations?.tabs?.topography || '3D Topography',
      icon: 'mountain',
    },
    {
      id: 'shape',
      label: translations?.tabs?.shape || 'Shape & Geometry',
      icon: 'hexagon',
    },
    ...(extraTabs?.map(({ id, label, icon }) => ({
      id,
      label,
      icon: icon ?? 'layout-dashboard',
    })) ?? []),
  ]

  // Build subtitle with egrid placeholder if translations provided
  const plotId = land.egrid_nb || land.plot_number || land.id
  const subtitle = translations?.header?.subtitle
    ? translations.header.subtitle.replace('{{egrid}}', String(plotId))
    : `Comprehensive topographic and geometric analysis for plot ${plotId}`

  return (
    <Container>
      <AnalysisSectionHeader
        title={translations?.header?.title || 'Geographical Analysis'}
        subtitle={subtitle}
        score={
          land.geo_shape_score !== undefined && land.geo_shape_score !== null
            ? {
                value: Math.round(land.geo_shape_score),
                maxValue: 100,
                label: translations?.header?.scoreLabel || 'Geo score',
                variant: getScoreVariant(land.geo_shape_score),
                showProgress: true,
              }
            : undefined
        }
      />

      <AnalysisTabButtonMenu
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as any)}
      />
      <AnalysisTabContentContainer>
        <SectionAnalysisContentContainer>
          {activeTab === 'overview' && (
            <OverviewTab
              metrics={metrics}
              altimetryData={altimetryData}
              terrainAltimetryData={terrainAltimetryData}
              translations={translations}
            />
          )}
          {activeTab === 'topography' && (
            <TopographyTab
              altimetryData={altimetryData}
              terrainAltimetryData={terrainAltimetryData}
              altimetryLoading={altimetryLoading}
              topographyChartRef={topographyChartRef}
              translations={translations}
              onDownloadTopography={handleDownloadTopography}
            />
          )}
          {activeTab === 'shape' && (
            <ShapeTab
              land={land}
              metrics={metrics}
              altimetryData={altimetryData}
              terrainAltimetryData={terrainAltimetryData}
              polygonChartRef={polygonChartRef}
              translations={translations}
              onDownloadPolygon={handleDownloadPolygon}
            />
          )}
          {extraTabs?.map((et) =>
            activeTab === et.id ? (
              <React.Fragment key={et.id}>{et.content}</React.Fragment>
            ) : null
          )}
        </SectionAnalysisContentContainer>
      </AnalysisTabContentContainer>
    </Container>
  )
}

export default GeoAnalysisSection
